// ─────────────────────────────────────────────────────────────────────────────
// Notepad — pure editor formatting helpers (professional writing surface).
//
// Small, dependency-free, side-effect-free string helpers powering the document
// editor's toolbar (bold/italic/heading/list/quote/checklist), the live
// word-count / reading-time footer, and the plain-text ⇄ markdown affordances.
// Pure & deterministic → unit-testable offline.
// ─────────────────────────────────────────────────────────────────────────────

export interface EditResult {
  text: string;
  /** New selection range to restore in the textarea. */
  selStart: number;
  selEnd: number;
}

/** Count words in a body of text (whitespace-separated, empties ignored). */
export function countWords(text: string): number {
  const t = (text ?? '').trim();
  if (!t) return 0;
  return t.split(/\s+/).length;
}

/** Estimated reading time in minutes at ~200 wpm (min 1 when there are words). */
export function readingTimeMin(text: string): number {
  const words = countWords(text);
  if (words === 0) return 0;
  return Math.max(1, Math.round(words / 200));
}

/**
 * Wrap the current selection with an inline marker (e.g. `**`, `*`, `` ` ``).
 * If the selection is already wrapped, unwrap it (toggle). With no selection,
 * insert the marker pair and place the cursor between them.
 */
export function applyInlineWrap(
  text: string,
  selStart: number,
  selEnd: number,
  marker: string,
): EditResult {
  const before = text.slice(0, selStart);
  const selected = text.slice(selStart, selEnd);
  const after = text.slice(selEnd);
  const m = marker.length;

  // Already wrapped just inside the selection → unwrap.
  if (selected.startsWith(marker) && selected.endsWith(marker) && selected.length >= 2 * m) {
    const inner = selected.slice(m, selected.length - m);
    return { text: before + inner + after, selStart, selEnd: selStart + inner.length };
  }
  // Markers sit immediately outside the selection → unwrap.
  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      text: before.slice(0, before.length - m) + selected + after.slice(m),
      selStart: selStart - m,
      selEnd: selEnd - m,
    };
  }
  if (selected.length === 0) {
    const next = before + marker + marker + after;
    const caret = selStart + m;
    return { text: next, selStart: caret, selEnd: caret };
  }
  const wrapped = marker + selected + marker;
  return { text: before + wrapped + after, selStart: selStart + m, selEnd: selStart + m + selected.length };
}

/** Expand a selection to whole lines (used by line-prefix toggles). */
function lineBounds(text: string, selStart: number, selEnd: number): { start: number; end: number } {
  const start = text.lastIndexOf('\n', selStart - 1) + 1;
  let end = text.indexOf('\n', selEnd);
  if (end === -1) end = text.length;
  return { start, end };
}

/**
 * Toggle a line prefix (e.g. `- `, `## `, `> `, `- [ ] `) on every line covered
 * by the selection. If every affected line already has the prefix, remove it.
 */
export function applyLinePrefix(
  text: string,
  selStart: number,
  selEnd: number,
  prefix: string,
): EditResult {
  const { start, end } = lineBounds(text, selStart, selEnd);
  const block = text.slice(start, end);
  const lines = block.split('\n');
  const allHave = lines.every((l) => l.startsWith(prefix));
  const next = lines
    .map((l) => (allHave ? l.slice(prefix.length) : prefix + l))
    .join('\n');
  const delta = next.length - block.length;
  return {
    text: text.slice(0, start) + next + text.slice(end),
    selStart: start,
    selEnd: end + delta,
  };
}

/** Toggle a `- [ ]` / `- [x]` checkbox on the line(s) in the selection. */
export function toggleTaskLine(text: string, selStart: number, selEnd: number): EditResult {
  const { start, end } = lineBounds(text, selStart, selEnd);
  const block = text.slice(start, end);
  const lines = block.split('\n');
  const next = lines
    .map((l) => {
      if (/^- \[ \] /.test(l)) return l.replace(/^- \[ \] /, '- [x] ');
      if (/^- \[x\] /i.test(l)) return l.replace(/^- \[x\] /i, '- [ ] ');
      return `- [ ] ${l}`;
    })
    .join('\n');
  const delta = next.length - block.length;
  return { text: text.slice(0, start) + next + text.slice(end), selStart: start, selEnd: end + delta };
}
